import type { ExternalContractSummary } from "@closer/shared";
import { Badge, EmptyState, PageHeader, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { api } from "../../lib/apiClient";

/**
 * "Erişimlerim" — haricî katılımcının görebildiği sözleşmeler (CLOS-147 `[128]`).
 *
 * Sorgu tenant bağlamı TAŞIMIYOR: haricî kullanıcının üyeliği yok, erişimleri birden çok
 * organizasyonda olabilir ve kapı sunucuda (kendi geçerli erişim satırları).
 */
export function ExternalContractsPage() {
  const erisimler = useQuery({
    queryKey: ["external", "contracts"],
    queryFn: async () => (await api<ExternalContractSummary[]>("/api/external/contracts")).data,
  });

  if (erisimler.isPending) {
    return (
      <div role="status" aria-label="Erişimler yükleniyor">
        <Skeleton lines={4} />
      </div>
    );
  }

  if (erisimler.isError) {
    return (
      <EmptyState
        title="Erişimler yüklenemedi"
        description="Lütfen sayfayı yenileyin; sorun sürerse sizi davet eden kişiyle iletişime geçin."
      />
    );
  }

  const satirlar = erisimler.data ?? [];
  if (satirlar.length === 0) {
    return (
      <EmptyState
        title="Görüntüleyebileceğiniz sözleşme yok"
        description="Erişiminiz sona ermiş ya da henüz bir davet kabul etmemiş olabilirsiniz. Yeni bir davet bağlantısı için sizi davet eden kişiyle iletişime geçin."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Erişimlerim" />
      <div className="overflow-hidden rounded-lg border border-border-default bg-surface">
        {satirlar.map((satir) => (
          <Link
            key={satir.contractId}
            to={`/harici/sozlesmeler/${satir.contractId}`}
            className="flex items-center justify-between gap-3 border-b border-border-subtle px-5 py-4 last:border-b-0 hover:bg-muted"
          >
            <div className="min-w-0">
              <p className="truncate font-medium text-fg">{satir.title}</p>
              <p className="text-sm text-fg-soft">{satir.organizationName}</p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <Badge>{satir.accessLevel === "write" ? "Düzenleme" : "Görüntüleme"}</Badge>
              {satir.endsAt && (
                <Badge tone="warning">
                  {new Date(satir.endsAt).toLocaleDateString("tr-TR")} tarihine kadar
                </Badge>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
